import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import CustomTypography from "../../globalStyles/fonts";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { parameterValueService } from "../../utils/services/parameter-value.service";
import ClientAutocomplete from "../clientAutocomplete";
import CalendarPicker from "../calendar-picker/CalendarPicker";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const InitialData = {
  value: "",
  date: null,
  client_id: "",
};

const ParameterValueModal = ({
  open,
  handleClose,
  onAddData,
  selectedParameter,
  parameterType,
  parameter_id,
}) => {
  const [parameter, setParameter] = useState(InitialData);
  const [formErrors, setFormErrors] = useState({});
  const [selectedClient, setSelectedClient] = useState(null);

  const validateForm = () => {
    const errors = {};
    if (!selectedClient || !selectedClient._id) {
      errors.client_id = "Client is required";
    }
    if (!parameter.value && parameter.value !== 0) {
      errors.value = "Value is required";
    }
    if (!parameter.date) {
      errors.date = "Date is required";
    } else {
      const selectedDate = new Date(parameter.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate > today) {
        errors.date = "Date cannot be in the future";
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (selectedParameter) {
      setParameter({
        client_id: selectedParameter.client_id || "",
        value: selectedParameter.value || "",
        date: selectedParameter.date
          ? new Date(selectedParameter.date).toISOString().split("T")[0]
          : "",
      });
    } else {
      resetData();
    }
  }, [selectedParameter]);

  const resetData = () => {
    setParameter(InitialData);
    setFormErrors({});
  };

  const handleEditData = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await parameterValueService.edit(selectedParameter.id, {
        type: parameter_id,
        client_id: selectedClient?._id || parameter.client_id,
        value: parseFloat(parameter.value),
        date: parameter.date,
      });
      onAddData && onAddData();
      toast.success("Parameter value updated successfully!");
      resetData();
      handleClose();
    } catch (error) {
      toast.error("Failed to update parameter value.");
      console.error(error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      await parameterValueService.create({
        type: parameter_id,
        client_id: selectedClient?._id || parameter.client_id?._id,
        value: parseFloat(parameter.value),
        date: parameter.date,
      });
      onAddData && onAddData();
      toast.success("Parameter value created successfully!");
      resetData();
      handleClose();
    } catch (error) {
      toast.error("Failed to create parameter value.");
      console.error(error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleClose();
        resetData();
      }}
      maxWidth="md"
      aria-labelledby="form-dialog-title"
      fullWidth
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <DialogTitle id="form-dialog-title" style={{ fontSize: 24 }}>
          {selectedParameter ? "Update" : "Create"} Parameter Value
        </DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Divider style={{ marginBottom: 10 }} />
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <CustomTypography>
              Parameter Type <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              size="small"
              name="type"
              value={parameterType}
              fullWidth
              margin="dense"
              disabled
              helperText="This field is auto-filled from the URL parameter"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTypography>
              Client <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <ClientAutocomplete
              onClientChange={(e) => setSelectedClient(e)}
              errorMessage={formErrors.client_id}
              selectedClient={selectedClient}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTypography>
              Value <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              size="small"
              name="value"
              type="number"
              value={parameter.value}
              onChange={(e) =>
                setParameter({ ...parameter, value: e.target.value })
              }
              fullWidth
              placeholder="Enter parameter value"
              margin="dense"
              error={!!formErrors.value}
              helperText={formErrors.value}
              inputProps={{
                step: "0.01",
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTypography style={{ marginBottom: 1 }}>
              Date <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                slotProps={{ textField: { size: "small", fullWidth: true } }}
                value={parameter.date ? dayjs(new Date(parameter.date)) : null}
                onChange={(e) => {
                  setParameter({
                    ...parameter,
                    date: dayjs(e).format("YYYY-MM-DD"),
                  });
                }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ paddingBottom: 15, paddingRight: 15 }}>
        <Button
          onClick={() => {
            handleClose();
            resetData();
          }}
          color="primary"
          variant="outlined"
          style={{
            fontFamily: "Poppins",
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={(e) =>
            selectedParameter ? handleEditData(e) : handleSave(e)
          }
          color="primary"
          variant="contained"
          style={{ textTransform: "none" }}
        >
          {selectedParameter ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ParameterValueModal;
